/**
 * Cross-calendar landing pages — "{origin} holidays in {destination}".
 *
 * Evergreen provider generates pages for origin × destination pairs
 * where the origin has ≥2 public holidays across the year.
 */

import { getDestinationMonths, getCountries } from './data-source';
import { getAllOriginCountries, getOriginVacationWindows } from './cross-calendar';
import {
  registerEvergreenProvider,
  type EvergreenSlugEntry,
  getLanguagesForCountry,
  getPhraseEntry,
  getTemplateStr,
  getCountryNameEntry,
} from './i18n';
import { registerEvergreenRenderer } from './evergreen-pages';
import { registerEvergreenReadiness } from './slug-evergreen';
import { SITE_NAME } from './brand';
import { CALENDAR_YEAR } from './config';
import { DESTINATIONS, type DestinationDef } from './country-month-pages';
import { CrossCalendarSeoSection } from '@/components/seo/CrossCalendarSeoSection';

const YEAR = CALENDAR_YEAR;

// Origin slug mapping (exported for visa-pages.ts)
export const ORIGIN_SLUG_MAP: Record<string, string> = {
  ID: 'indonesia', MY: 'malaysia', SG: 'singapore', MM: 'myanmar',
  AF: 'afghanistan', XR: 'tibet', IN: 'india', ET: 'ethiopia',
  MN: 'mongolia', LA: 'laos', SD: 'sudan', YE: 'yemen',
  SY: 'syria', IQ: 'iraq', PH: 'philippines', TH: 'thailand',
  VN: 'vietnam', TR: 'turkey', AE: 'united-arab-emirates',
  US: 'united-states', GB: 'united-kingdom', AU: 'australia',
  EG: 'egypt', SA: 'saudi-arabia',
};

export interface CrossCalendarDef {
  slug: string;
  originCode: string;
  originName: string;
  originFlag: string;
  dest: DestinationDef;
  /** Months where origin has holidays and destination has data */
  overlapMonths: number[];
}

function buildCrossCalendarDefs(): CrossCalendarDef[] {
  const defs: CrossCalendarDef[] = [];
  const origins = getAllOriginCountries();

  for (const origin of origins) {
    // Only curated origins get a real localized slug. Skipping unmapped
    // origins prevents a bare-code cross-product (e.g. `kr-holidays-in-ethiopia`
    // → zh `kr-jiaqi-zai-ethiopia`) — historically ~95% of all site 404s.
    if (!(origin.code in ORIGIN_SLUG_MAP)) continue;

    for (const dest of DESTINATIONS) {
      // Skip self (origin = destination country)
      if (origin.code === dest.countryCode) continue;

      // Check destination has month data
      const destMonths = getDestinationMonths(dest.countryCode);
      if (destMonths.length === 0) continue;

      // Find months where origin has holidays
      const overlapMonths: number[] = [];
      for (let m = 1; m <= 12; m++) {
        const vw = getOriginVacationWindows(origin.code, m);
        const hasHolidays = vw.holidays.length > 0 || vw.originHolidays.length > 0;
        if (hasHolidays) overlapMonths.push(m);
      }

      // Need ≥2 holiday months for a useful page
      if (overlapMonths.length < 2) continue;

      const originSlug = ORIGIN_SLUG_MAP[origin.code];
      const slug = `${originSlug}-holidays-in-${dest.slugPrefix}`;

      defs.push({
        slug,
        originCode: origin.code,
        originName: origin.name,
        originFlag: origin.flag,
        dest,
        overlapMonths,
      });
    }
  }

  return defs;
}

// Lazy-built definitions.
// NEVER memoize an empty result: buildCrossCalendarDefs depends on origin
// (geo) and vacation-window (events) data. If getDefs() is first called
// before those load it returns [], and a plain `if (!_defs)` guard would
// freeze that empty array forever — which silently dropped the entire
// cross-calendar page type out of the evergreen cache (all holidays-in /
// jiaqi-zai pages 404'd site-wide). Rebuild until we get real defs.
// (almanak-keyh)
let _defs: CrossCalendarDef[] | null = null;
function getDefs(): CrossCalendarDef[] {
  if (!_defs || _defs.length === 0) _defs = buildCrossCalendarDefs();
  return _defs;
}

export function getCrossCalendarDef(slug: string): CrossCalendarDef | undefined {
  return getDefs().find(d => d.slug === slug);
}

export function getCrossCalendarDefsForDest(destSlugPrefix: string): CrossCalendarDef[] {
  return getDefs().filter(d => d.dest.slugPrefix === destSlugPrefix);
}

export function getCrossCalendarDefsForOrigin(originCode: string): CrossCalendarDef[] {
  return getDefs().filter(d => d.originCode === originCode);
}

// ---------------------------------------------------------------------------
// Evergreen provider
// ---------------------------------------------------------------------------

function provideCrossCalendarSlugs(): EvergreenSlugEntry[] {
  const entries: EvergreenSlugEntry[] = [];

  for (const def of getDefs()) {
    const entityId = `cross-calendar:${def.originCode.toLowerCase()}:${def.dest.countryCode.toLowerCase()}`;
    const langs = getLanguagesForCountry(def.dest.countryCode);
    const originSlug = ORIGIN_SLUG_MAP[def.originCode];

    const seenSlugs = new Set<string>();
    for (const lang of langs) {
      const holidaysInPhrase = getPhraseEntry('holidays_in', lang);
      const slug = `${originSlug}-${holidaysInPhrase.slug}-${def.dest.slugPrefix}`;

      // Skip languages whose phrase falls back to English, producing a duplicate slug
      if (seenSlugs.has(slug)) continue;
      seenSlugs.add(slug);

      const tplVars = {
        origin: def.originName,
        destination: def.dest.displayName,
        year: YEAR,
        siteName: SITE_NAME,
      };
      const pageTitle = getTemplateStr('cross_calendar_title', lang, tplVars)
        || `${def.originName} Holidays in ${def.dest.displayName} ${YEAR} — When to Visit | ${SITE_NAME}`;
      const pageDescription = getTemplateStr('cross_calendar_desc', lang, tplVars)
        || `Plan your trip from ${def.originName} to ${def.dest.displayName}. See which ${def.originName} holidays align with the best travel conditions in ${def.dest.displayName}.`;

      entries.push({
        pageType: 'cross-calendar',
        slug,
        lang,
        entityId,
        pageTitle,
        pageDescription,
        h2Title: `${def.originFlag} ${holidaysInPhrase.display} ${getCountryNameEntry(def.dest.countryCode, lang).display}`,
        data: {
          // countryCode drives the evergreen wrapper's country breadcrumb crumb
          // + RelatedPagesGrid (page.tsx gate). Anchor to the DESTINATION — this
          // is a "visit {destination}" travel page. (almanak-220z.1)
          countryCode: def.dest.countryCode,
          crossCalendarSlug: def.slug,
          originCode: def.originCode,
          originName: def.originName,
          originFlag: def.originFlag,
          destSlugPrefix: def.dest.slugPrefix,
          destDisplayName: def.dest.displayName,
          destCountryCode: def.dest.countryCode,
        },
      });
    }
  }

  return entries;
}

registerEvergreenProvider(provideCrossCalendarSlugs);
// Gate the evergreen cache on cross-calendar data being present. Without this,
// ensureEvergreenBuilt could freeze a cache built from other (ready) providers
// while cross-calendar still returned [], permanently excluding its slugs.
// (almanak-keyh)
registerEvergreenReadiness(() => getDefs().length > 0 && getCountries().length > 0);

// ---------------------------------------------------------------------------
// Evergreen renderer
// ---------------------------------------------------------------------------

registerEvergreenRenderer('cross-calendar', (entry, topPages) => {
  const slug = entry.data.crossCalendarSlug as string;
  const def = getCrossCalendarDef(slug);
  if (!def) return null;
  return CrossCalendarSeoSection({ def, entry });
});
